import { ReactNode, useEffect, useId, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { ApplicationError } from "../general/Errors";
import {
    Asset,
    Customer,
    EntityQuery,
    Gateway,
    is,
    Query,
} from "@the-real-insight/tri-model";
import { useAdministrationAPI } from "../AdministrationAPI";
import { userAPI } from "../UserAPI";
import ReactApexChart from "react-apexcharts";
import Metric from "../components/Metric";
import { useCustomerTheme } from "../Theme";
import DeviceProperties from "../components/DeviceProperties";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useModalContext } from "../components/ModalProvider";
import { useUrlPageState } from "../utilities/dataTablePagination";
import { formatCompactNumber } from "../utilities/format";

import type { Column, ColumnDef } from "@tanstack/react-table";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ExternalLink,
    Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const now = Date.now();

interface Properties {}

interface Device {
  uuid: string;
}

const numberFormat2 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

/** Leerzustand einer Tabelle: Überschrift, Erklärung und Handlungsaufforderung. */
function TableEmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
      {action}
    </div>
  );
}

const deviceKey = (device: any) => String(device?._id ?? device?.id ?? "");

/**
 * Zeilen pro Geräteseite.
 *
 * Zugleich die Seitengröße der Abfrage: Server-Paginierung heißt, dass genau so
 * viele Geräte über die Leitung gehen. Vorher waren es fünf Zeilen aus einer
 * vollständig geladenen Liste — bei hunderttausend Geräten wären das
 * zwanzigtausend Seiten über 47 MB.
 */
const DEVICE_PAGE_SIZE = 25;

export default function GatewayPanel(_properties: Properties) {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const gatewayListPagination = useUrlPageState('gatewayListPage');
  const gatewayDevicesPagination = useUrlPageState('gatewayDevicesPage');
  const uploadRecordsPagination = useUrlPageState('uploadRecordsPage');
  const navigate = useNavigate();
  const fieldId = useId();
  const { pushError, pushSuccess } = useModalContext() as any;
  const { theme } = useCustomerTheme();
  const {
    createGateway,
    getDevicesFromGateway,
    configureDevicesOnGateway,
    refreshDevicesOnGateway,
    getGatewayStatus,
  } = useAdministrationAPI();
  const [tab, setTab] = useState(
    searchParams.get("tab") || "productData"
  ) as any;
  const [gateways, setGateways] = useState([]) as any;
  const [gatewaysLoading, setGatewaysLoading] = useState(true);
  const [selectedGateway, setSelectedGateway] = useState() as any;
  const [_devices, setDevices] = useState([]) as any;
  const [filteredDevices, setFilteredDevices] = useState([]) as any;
  /**
   * Wie viele Geräte der Gateway insgesamt hat — `filteredDevices` ist nur die
   * angezeigte Seite. Ohne diese Zahl wüsste die Tabelle nicht, dass es weitere
   * Seiten gibt, und bliebe bei einer stehen.
   */
  const [deviceTotal, setDeviceTotal] = useState(0);
  /**
   * Spiegelt die Auswahl für laufende Abfragen. Der State allein genügt nicht:
   * eine Antwort, die nach einem Gateway-Wechsel eintrifft, würde sonst die
   * Geräte des alten Gateways in die neue Ansicht schreiben.
   */
  const selectedGatewayRef = useRef<any>(null);

  useEffect(() => {
    selectedGatewayRef.current = selectedGateway ?? null;
  }, [selectedGateway]);
  const [devicesLoading, setDevicesLoading] = useState(false) as any;
  const [selectedDevices, setSelectedDevices] = useState([]) as any;
  const [selectedDeviceKeys, setSelectedDeviceKeys] = useState<Set<string>>(new Set());
  const [editDevice, setEditDevice] = useState([]) as any;
  const [customers, _setCustomers] = useState() as any;
  const [gatewayStatus, setGatewayStatus] = useState() as any;
  const [timer, setTimer] = useState(Date.now()) as any;
  const [memorySeries, setMemorySeries] = useState(
    [...Array(30)].map((_: any, index: number) => {
      return [index * 10000, null];
    })
  ) as any;
  const [cpuSeries, setCPUSeries] = useState(
    [...Array(30)].map((_: any, index: number) => {
      return [index * 10000, null];
    })
  ) as any;
  const [asset, setAsset] = useState() as any;
  const [deviceFilter, setDeviceFilter] = useState("");
  const [showUploadRecordsDialog, setShowUploadRecordsDialog] = useState(false) as any;
  const [refreshingDevices, setRefreshingDevices] = useState(false);

  const deviceFilterId = `${fieldId}-device-filter`;
  const gatewayUrlId = `${fieldId}-gateway-url`;
  const gatewayCustomerId = `${fieldId}-gateway-customer`;

  const options: any = {
    chart: {
      id: "realtime",
      height: 350,
      type: "area",
      // animations: {
      //     enabled: true,
      //     easing: 'linear',
      //     dynamicAnimation: {
      //         speed: 1000
      //     }
      // },
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: [theme.secondary],
    stroke: {
      // curve: 'smooth',
      width: 2,
    },
    markers: {
      size: 0,
    },
    xaxis: {
      type: "numeric",
      tickAmount: 5,
      labels: {
        formatter: (value: any) => {
          return new Date(now - value).toLocaleTimeString(i18n.language);
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: any) => {
          return value ? `${value.toFixed(0)} MB` : "";
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
    legend: {
      show: false,
    },
  };

  const cpuChartOptions: any = {
    chart: {
      id: "cpu",
      height: 350,
      type: "area",
      // animations: {
      //     enabled: true,
      //     easing: 'linear',
      //     dynamicAnimation: {
      //         speed: 1000
      //     }
      // },
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      // curve: 'smooth',
      width: 2,
    },
    markers: {
      size: 0,
    },
    xaxis: {
      type: "numeric",
      tickAmount: 5,
      labels: {
        formatter: (value: any) => {
          return new Date(now - value).toLocaleTimeString(i18n.language);
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: any) => {
          return value ? `${value.toFixed(0)} %` : "";
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
    legend: {
      show: false,
    },
  };

  const columns: ColumnDef<any>[] = [
    {
      id: "gateway",
      accessorFn: (gateway: any) => gateway._id,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.gateway")}</SortableHeader>
      ),
      cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original._id}</span>,
    },
    {
      id: "url",
      accessorFn: (gateway: any) => gateway.url,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.url")}</SortableHeader>
      ),
      cell: ({ row }) => <span className="min-w-0 break-all text-sm">{row.original.url}</span>,
    },
    {
      id: "customer",
      accessorFn: (gateway: any) => gateway._tenant,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.customer")}</SortableHeader>
      ),
      cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original._tenant}</span>,
    },
    {
      id: "status",
      accessorFn: (_gateway: Gateway) => "",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.status")}</SortableHeader>
      ),
      cell: () => (
        <span className="text-sm font-semibold text-success">
          {t("settingsPanel.gatewayPanel.running")}
        </span>
      ),
    },
  ];

  const uploadRecordsColumns: ColumnDef<any>[] = [
    {
      id: "timestamp",
      accessorFn: (record: any) => (record.timestamp ? new Date(record.timestamp).getTime() : 0),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.timestamp")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          <div>
            {row.original.timestamp
              ? new Date(row.original.timestamp).toLocaleDateString(i18n.language)
              : "-"}
          </div>
          <div>
            {row.original.timestamp
              ? new Date(row.original.timestamp).toLocaleTimeString(i18n.language)
              : ""}
          </div>
        </div>
      ),
    },
    {
      id: "asset",
      accessorFn: (record: any) => record._id || "",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.asset")}</SortableHeader>
      ),
      cell: ({ row }) => <div className="min-w-0 break-words text-sm">{row.original._id || "-"}</div>,
    },
    {
      id: "id",
      accessorFn: (record: any) => record.id || "",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.id")}</SortableHeader>
      ),
      cell: ({ row }) => <div className="min-w-0 break-words text-sm">{row.original.id || "-"}</div>,
    },
    {
      id: "type",
      accessorFn: (record: any) => record.type || "",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.type")}</SortableHeader>
      ),
      cell: ({ row }) => <div className="min-w-0 break-words text-sm">{row.original.type || "-"}</div>,
    },
    {
      id: "manufacturer",
      accessorFn: (record: any) => record.manufacturer || "",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.manufacturer")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">{row.original.manufacturer || "-"}</div>
      ),
    },
    {
      id: "metric",
      accessorFn: (record: any) => record.metric || "",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.metric")}</SortableHeader>
      ),
      cell: ({ row }) => <div className="min-w-0 break-words text-sm">{row.original.metric || "-"}</div>,
    },
    {
      id: "value",
      accessorFn: (record: any) =>
        record.value !== undefined && record.value !== null ? record.value : "",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.value")}</SortableHeader>
      ),
      cell: ({ row }) => {
        const record = row.original;
        if (record.value !== undefined && record.value !== null) {
          const numValue = typeof record.value === 'number' ? record.value : parseFloat(record.value);
          if (!isNaN(numValue)) {
            return (
              <div className="text-right text-sm tabular-nums">
                {numberFormat2.format(numValue)}
              </div>
            );
          }
        }
        return <div className="text-right text-sm">-</div>;
      },
    },
    {
      id: "address",
      accessorFn: (record: any) => {
        const parts = [];
        if (record.street) parts.push(record.street);
        if (record.streetNumber) parts.push(record.streetNumber);
        if (record.zipCode || record.postalCode) parts.push(record.zipCode || record.postalCode);
        if (record.city) parts.push(record.city);
        if (record.country) parts.push(record.country);
        return parts.join(", ");
      },
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.address")}</SortableHeader>
      ),
      cell: ({ row }) => {
        const record = row.original;
        const line1Parts = [];
        if (record.street && record.streetNumber) {
          line1Parts.push(`${record.street} ${record.streetNumber}`);
        } else if (record.street) {
          line1Parts.push(record.street);
        } else if (record.streetNumber) {
          line1Parts.push(record.streetNumber);
        }

        const line2Parts = [];
        if (record.zipCode || record.postalCode) {
          line2Parts.push(record.zipCode || record.postalCode);
        }
        if (record.city) {
          line2Parts.push(record.city);
        }
        if (record.country) {
          line2Parts.push(record.country);
        }

        if (line1Parts.length === 0 && line2Parts.length === 0) {
          return <div className="text-sm">-</div>;
        }

        return (
          <div className="min-w-0 break-words text-sm">
            {line1Parts.length > 0 && <div>{line1Parts.join(" ")}</div>}
            {line2Parts.length > 0 && <div>{line2Parts.join(" ")}</div>}
          </div>
        );
      },
    },
  ];

  const selectDeviceKeys = (keys: Set<string>) => {
    setSelectedDeviceKeys(keys);
    selectDevices(filteredDevices.filter((device: any) => keys.has(deviceKey(device))));
  };

  const deviceColumns: ColumnDef<any>[] = [
    {
      id: "select",
      header: () => {
        const selectableKeys = filteredDevices.map((device: any) => deviceKey(device));
        const allSelected =
          selectableKeys.length > 0 && selectableKeys.every((key: string) => selectedDeviceKeys.has(key));

        return (
          <Checkbox
            checked={allSelected}
            aria-label={t("settingsPanel.gatewayPanel.selectAllDevices")}
            onCheckedChange={(checked) =>
              selectDeviceKeys(checked === true ? new Set<string>(selectableKeys) : new Set<string>())
            }
          />
        );
      },
      enableSorting: false,
      cell: ({ row }) => {
        const key = deviceKey(row.original);

        return (
          <Checkbox
            checked={selectedDeviceKeys.has(key)}
            aria-label={`${t("global.select")}: ${row.original.name || row.original.id || key}`}
            onCheckedChange={(checked) => {
              const keys = new Set(selectedDeviceKeys);
              if (checked === true) {
                keys.add(key);
              } else {
                keys.delete(key);
              }
              selectDeviceKeys(keys);
            }}
          />
        );
      },
    },
    {
      id: "open",
      header: () => <span className="sr-only">{t("settingsPanel.gatewayPanel.openAsset")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("settingsPanel.gatewayPanel.openAsset")}
          title={t("settingsPanel.gatewayPanel.openAsset")}
          onClick={() =>
            navigate(
              `/entity?selectedEntityType=Asset&selectedEntity=${row.original._id}`
            )
          }
        >
          <ExternalLink aria-hidden className="h-4 w-4" />
        </Button>
      ),
    },
    {
      id: "device",
      accessorFn: (device: any) => `${device.name || device.id}`,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.device")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">
          <div>{row.original.name || "-"}</div>
          <div>{row.original.id || "-"}</div>
        </div>
      ),
    },
    {
      id: "address",
      accessorFn: (device: any) =>
        device.street
          ? `${device.street} ${device.streetNumber}, ${device.postalCode} ${device.city}, ${device.country}`
          : ``,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.address")}</SortableHeader>
      ),
      cell: ({ row }) =>
        row.original.street ? (
          <div className="min-w-0 break-words text-sm">
            <div>
              {row.original.street} {row.original.streetNumber}
            </div>
            <div>
              {row.original.postalCode} {row.original.city}, {row.original.country}
            </div>
          </div>
        ) : null,
    },
    {
      id: "lastStateChange",
      accessorFn: (device: any) =>
        device.lastStateChange ? new Date(device.lastStateChange).getTime() : 0,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.lastStateChange")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          <div>
            {row.original.lastStateChange
              ? new Date(row.original.lastStateChange).toLocaleDateString(i18n.language)
              : "-"}
          </div>
          <div>
            {row.original.lastStateChange
              ? new Date(row.original.lastStateChange).toLocaleTimeString(i18n.language)
              : ""}
          </div>
        </div>
      ),
    },
    {
      id: "lastConfiguration",
      accessorFn: (device: any) =>
        device.lastConfigurationChange ? new Date(device.lastConfigurationChange).getTime() : 0,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("settingsPanel.gatewayPanel.lastConfiguration")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          <div>
            {row.original.lastConfigurationChange
              ? new Date(row.original.lastConfigurationChange).toLocaleDateString(
                  i18n.language
                )
              : "-"}
          </div>
          <div>
            {row.original.lastConfigurationChange
              ? new Date(row.original.lastConfigurationChange).toLocaleTimeString(
                  i18n.language
                )
              : ""}
          </div>
        </div>
      ),
    },
  ];

  const selectGateway = async (gateway: any) => {
    console.log("🔗 selectGateway called with:", gateway);

    setSelectedGateway(gateway);
    setSelectedDeviceKeys(new Set());
    setSelectedDevices([]);
    setEditDevice(null);

    if (!gateway) {
      console.log("🔗 Clearing gateway selection");
      searchParams.delete("gateway");
      setSearchParams(searchParams);
      setDevices([]);
      setFilteredDevices([]);
      return;
    }

    console.log("🔗 Setting gateway in URL parameters:", gateway._id);
    searchParams.set("gateway", gateway._id);
    setSearchParams(searchParams);

    await loadDevicePage(gateway, gatewayDevicesPagination.page, deviceFilter);
  };

  /**
   * Eine Seite Geräte vom Gateway holen.
   *
   * Seite und Filter gehen an den Gateway, nicht an den Browser: die Demo-Flotte
   * sind hunderttausend Geräte, deren vollständige Liste rund 47 MB und sechzehn
   * Sekunden kostet. Alles zu laden, um 25 Zeilen zu zeigen, war der Grund, dass
   * das Blättern hier stehenblieb.
   */
  const loadDevicePage = async (
    gateway: any,
    page: number,
    filter: string,
  ) => {
    if (!gateway) return;

    const gatewayId = gateway._id;

    try {
      setDevicesLoading(true);

      const { items, total } = await getDevicesFromGateway(gateway, {
        page,
        pageSize: DEVICE_PAGE_SIZE,
        filter: filter.trim() || undefined,
      });

      // Die Auswahl kann sich während des Ladens geändert haben; ein spätes
      // Ergebnis darf die neue Auswahl nicht überschreiben.
      if (selectedGatewayRef.current?._id !== gatewayId) return;

      setDevices(items);
      setFilteredDevices(items);
      setDeviceTotal(total);
    } catch (error) {
      console.error("Error getting devices from gateway:", error);
      pushError(t("settingsPanel.gatewayPanel.failedToLoadDevices"));
    } finally {
      setDevicesLoading(false);
    }
  };

  const checkHomogenousDeviceType = (devices: any) => {
    let manufacturer = null;
    let type = null;

    for (const device of devices) {
      if (!manufacturer) {
        manufacturer = device.manufacturer;
        type = device.type;

        continue;
      } else if (manufacturer !== device.manufacturer || type !== device.type) {
        return false;
      }
    }

    return true;
  };

  const selectDevices = async (devices: any) => {
    setSelectedDevices(devices);

    if (!devices || devices.length === 0) {
      setEditDevice(null);

      return;
    }

    if (devices.length > 1) {
      if (checkHomogenousDeviceType(devices)) {
        setEditDevice({
          manufacturer: devices[0].manufacturer,
          type: devices[0].type,
        });
      } else {
        setEditDevice(null);
      }

      return;
    }

    setEditDevice(devices[0]);

    // TODO No longer needed

    const assets = await userAPI.query(
      new Query(
        new EntityQuery(
          Asset.type,
          [],
          undefined,
          is("_id").equal(devices[0]._id)
        )
      )
    );

    if (assets && assets.length === 1) {
      setAsset(assets[0]);
    }
  };

  const setParams = (newTab: string) => {
    console.log("🔗 Tab changed to:", newTab);
    searchParams.set("tab", newTab);
    setSearchParams(searchParams);
    setTab(newTab);
  };

  const updateConfigurations = async (configuration: any) => {
    const updatedDevices = selectedDevices.map((device: Device) => {
      return { ...device, ...configuration };
    });

    const _response = await configureDevicesOnGateway(
      selectedGateway,
      updatedDevices
    );
  };

  // Activate/refresh all digital twins of this gateway (server resolves the
  // gateway's assets and pushes them to the running gateway in batches).
  const handleRefreshDevices = async () => {
    if (!selectedGateway || refreshingDevices) return;
    setRefreshingDevices(true);
    try {
      const result = await refreshDevicesOnGateway(selectedGateway);
      const pushed = result?.pushed ?? 0;
      const devicesTotal = result?.devices ?? 0;
      const batches = result?.batches ?? 0;
      const failed = result?.failed ?? 0;
      const message =
        `${pushed} / ${devicesTotal} Geräte auf dem Gateway aktualisiert` +
        ` (${batches} Batches${failed ? `, ${failed} fehlgeschlagen` : ""}).`;

      // Reload the current page so the panel reflects the refreshed set.
      try {
        await loadDevicePage(selectedGateway, gatewayDevicesPagination.page, deviceFilter);

        setSelectedDeviceKeys(new Set());
        setSelectedDevices([]);
        setEditDevice(null);

        if (failed > 0 && pushed === 0) {
          pushError(new ApplicationError(message));
        } else {
          pushSuccess(message);
        }
      } catch (reloadError) {
        console.error("Error reloading devices after refresh:", reloadError);
        pushError(
          new ApplicationError(`${message} ${t("settingsPanel.gatewayPanel.failedToLoadDevices")}`)
        );
      }
    } catch (error: any) {
      pushError(new ApplicationError(error.message || String(error)));
    } finally {
      setRefreshingDevices(false);
    }
  };

  /**
   * Der Filter wird beim Gateway angewandt, nicht hier: die Seite im Browser
   * enthält nur 25 Geräte, ein Filter darüber fände die anderen 100.000 nie.
   * Beim Filtern zurück auf Seite 1 — die alte Seitenzahl gehört zu einer
   * anderen Ergebnismenge.
   */
  const filterDevices = (filterText: string) => {
    gatewayDevicesPagination.onPageChange(1);
    void loadDevicePage(selectedGateway, 1, filterText);
  };

  useEffect(() => {
    if (!selectedGateway) {
      return;
    }

    const call = async () => {
      try {
        const newGatewayStatus = await getGatewayStatus(selectedGateway);
        const newMemorySeries = [...memorySeries];

        newMemorySeries.pop();
        newMemorySeries.unshift([
          now - Date.now(),
          newGatewayStatus.memoryUsage.heapUsed / 1000000,
        ]);

        const newCPUSeries = [...cpuSeries];

        newCPUSeries.pop();
        newCPUSeries.unshift([now - Date.now(), newGatewayStatus.cpuUsage]);

        setMemorySeries(newMemorySeries);
        setCPUSeries(newCPUSeries);
        setGatewayStatus(newGatewayStatus);
      } catch (error) {
        console.error("Error getting gateway status:", error);
        pushError(t("settingsPanel.gatewayPanel.failedToGetGatewayStatus"));
      }
    };

    call();

    const interval = setInterval(() => setTimer(Date.now()), 10000);

    return () => {
      clearInterval(interval);
    };
  }, [selectedGateway, timer]);

  // Nur dieser Parameter lädt die Gateways neu. `searchParams` als Ganzes zu beobachten hieße:
  // jedes Blättern in einer der Tabellen (`gatewayListPage` …) lädt die Liste neu (#1186).
  const gatewayIdInUrl = searchParams.get("gateway");

  useEffect(() => {
    const call = async () => {
      try {
        const gateways = await userAPI.query(
          new Query(new EntityQuery(Gateway.type))
        );

        setGateways(gateways);

        // Handle gateway selection from URL parameter
        const gatewayId = gatewayIdInUrl;
        if (gatewayId) {
          const gatewayFromUrl = gateways.find(
            (gateway: any) => gateway._id === gatewayId
          );
          if (gatewayFromUrl) {
            console.log("🔗 Gateway found from URL parameter:", gatewayFromUrl);
            await selectGateway(gatewayFromUrl);
          } else {
            console.warn("⚠️ Gateway not found for ID:", gatewayId);
            pushError(`Gateway with ID "${gatewayId}" not found.`);
            // Clear the invalid gateway parameter
            setSearchParams((current) => {
              const next = new URLSearchParams(current);
              next.delete("gateway");
              return next;
            });
          }
        }
      } catch (error) {
        console.error("Error getting gateways:", error);
        pushError(t("settingsPanel.gatewayPanel.failedToLoadGateways"));
      } finally {
        setGatewaysLoading(false);
      }
    };

    call();
  }, [gatewayIdInUrl]);

  // Handle URL parameter changes when gateways are already loaded
  useEffect(() => {
    const gatewayId = searchParams.get("gateway");

    if (gateways.length > 0 && gatewayId) {
      const gatewayFromUrl = gateways.find(
        (gateway: any) => gateway._id === gatewayId
      );
      if (
        gatewayFromUrl &&
        (!selectedGateway || selectedGateway._id !== gatewayId)
      ) {
        console.log(
          "🔗 Gateway selection changed via URL parameter:",
          gatewayFromUrl
        );
        selectGateway(gatewayFromUrl);
      } else if (!gatewayFromUrl && selectedGateway) {
        console.log("🔗 Invalid gateway ID in URL, clearing selection");
        pushError(`Gateway with ID "${gatewayId}" not found.`);
        searchParams.delete("gateway");
        setSearchParams(searchParams);
      }
    } else if (gateways.length > 0 && !gatewayId && selectedGateway) {
      console.log("🔗 Gateway parameter removed from URL, clearing selection");
      setSelectedGateway(null);
    }
  }, [searchParams, gateways, selectedGateway]);

  // Handle tab changes from URL parameters
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    const validTabs = ["productData", "devices"];

    if (tabFromUrl && tabFromUrl !== tab) {
      if (validTabs.includes(tabFromUrl)) {
        console.log("🔗 Tab changed via URL parameter:", tabFromUrl);
        setTab(tabFromUrl);
      } else {
        console.warn(
          "⚠️ Invalid tab in URL parameter:",
          tabFromUrl,
          "Valid tabs:",
          validTabs
        );
        pushError(
          `Invalid tab "${tabFromUrl}". Valid tabs are: ${validTabs.join(", ")}`
        );
        // Clear the invalid tab parameter and reset to default
        searchParams.delete("tab");
        setSearchParams(searchParams);
        setTab("productData");
      }
    } else if (!tabFromUrl && tab !== "productData") {
      console.log("🔗 Tab parameter removed from URL, resetting to default");
      setTab("productData");
    }
  }, [searchParams, tab]);

  const deviceFilterActive = deviceFilter.trim().length > 0;

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      {selectedGateway ? (
        selectedGateway._id ? (
          <>
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-muted-foreground">Gateway</div>
                <h2 className="min-w-0 break-all text-lg font-semibold">{selectedGateway.url}</h2>
              </div>
              <div className="flex flex-wrap gap-2 max-sm:w-full">
                <Button
                  className="max-sm:flex-1"
                  onClick={handleRefreshDevices}
                  disabled={refreshingDevices}
                >
                  {refreshingDevices && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                  {refreshingDevices ? "Aktualisiere…" : "Geräte aktualisieren"}
                </Button>
                <Button
                  variant="outline"
                  className="max-sm:flex-1"
                  onClick={() => {
                    setSelectedGateway(null);
                    searchParams.delete("gateway");
                    setSearchParams(searchParams);
                  }}
                >
                  {t("settingsPanel.gatewayPanel.close")}
                </Button>
              </div>
            </div>
            <Tabs value={tab} onValueChange={setParams}>
              <TabsList>
                <TabsTrigger value="productData">{t("settingsPanel.gatewayPanel.status")}</TabsTrigger>
                <TabsTrigger value="devices">{t("settingsPanel.gatewayPanel.devices")}</TabsTrigger>
              </TabsList>

              <TabsContent value="productData">
                {gatewayStatus ? (
                  <div className="flex min-w-0 flex-col gap-8">
                    <div className="flex min-w-0 flex-wrap gap-8">
                      <Metric
                        title={t("settingsPanel.gatewayPanel.activeDevices")}
                        value={formatCompactNumber(gatewayStatus.activeDevices)}
                        color={theme.secondaryLight}
                      ></Metric>
                      <Metric
                        title={t("settingsPanel.gatewayPanel.totalStateUpdates")}
                        value={formatCompactNumber(gatewayStatus.totalStateUpdateCount)}
                        color={theme.tertiary}
                      ></Metric>
                      <Metric
                        title={t("settingsPanel.gatewayPanel.totalConfigUpdates")}
                        value={formatCompactNumber(gatewayStatus.totalConfigurationUpdateCount)}
                        color={theme.primary}
                      ></Metric>
                      <Metric
                        title={t("settingsPanel.gatewayPanel.messageThroughput")}
                        value={"53"}
                        unit={"Msg./s"}
                        color={theme.secondary}
                      ></Metric>
                      <Metric
                        title={t("settingsPanel.gatewayPanel.failedMeasurementUploads")}
                        value={formatCompactNumber(gatewayStatus.failedMeasurementUploads || 0)}
                        color={theme.error || theme.primary}
                      ></Metric>
                      <Metric
                        title={t("settingsPanel.gatewayPanel.successfulMeasurementUploads")}
                        value={formatCompactNumber(gatewayStatus.measurementUploads || 0)}
                        color={theme.tertiary}
                        onClick={() => {
                          if (gatewayStatus.successfulUploadRecords && gatewayStatus.successfulUploadRecords.length > 0) {
                            setShowUploadRecordsDialog(true);
                          }
                        }}
                      ></Metric>
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-8">
                      <Metric
                        title={t("settingsPanel.gatewayPanel.memoryUsage")}
                        value={numberFormat2.format(gatewayStatus.memoryUsage.heapUsed / 1000000)}
                        unit={"MB"}
                        color={theme.secondary}
                      ></Metric>
                      <div className="min-w-0 max-w-full overflow-x-auto">
                        <ReactApexChart
                          options={{
                            ...options,
                            colors: [theme.secondary],
                          }}
                          series={[{ data: memorySeries }]}
                          type="area"
                          height={150}
                          width={500}
                        />
                      </div>
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-8">
                      <Metric
                        title={t("settingsPanel.gatewayPanel.cpuUsage")}
                        value={gatewayStatus.cpuUsage.toFixed(0)}
                        unit={"%"}
                        color={theme.tertiary}
                      ></Metric>
                      <div className="min-w-0 max-w-full overflow-x-auto">
                        <ReactApexChart
                          options={{
                            ...cpuChartOptions,
                            colors: [theme.tertiary],
                          }}
                          series={[{ data: cpuSeries }]}
                          type="area"
                          height={150}
                          width={500}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex min-w-0 flex-col gap-8" role="status" aria-live="polite">
                    <span className="sr-only">{t("global.loading")}</span>
                    <div className="flex flex-wrap gap-8">
                      {[...Array(6)].map((_, index) => (
                        <Skeleton key={index} className="h-[72px] w-[150px]" />
                      ))}
                    </div>
                    <Skeleton className="h-[150px] w-full max-w-[660px]" />
                    <Skeleton className="h-[150px] w-full max-w-[660px]" />
                  </div>
                )}
              </TabsContent>

              <TabsContent value="devices">
                <div className="flex min-w-0 flex-wrap gap-4">
                  <div className="flex min-w-0 flex-[2] basis-[520px] flex-col gap-4">
                    <div className="flex min-w-0 max-w-2xl flex-col gap-1.5">
                      <Label htmlFor={deviceFilterId}>
                        {t("settingsPanel.gatewayPanel.filter")}
                      </Label>
                      <Input
                        id={deviceFilterId}
                        className="w-[360px] max-w-full"
                        placeholder={t("settingsPanel.gatewayPanel.enterFilter")}
                        value={deviceFilter}
                        onChange={(event) => {
                          setDeviceFilter(event.target.value || "");
                          filterDevices(event.target.value || "");
                        }}
                      />
                    </div>
                    <div className="min-w-0 overflow-x-auto">
                      <DataTable
                        columns={deviceColumns}
                        data={filteredDevices}
                        loading={devicesLoading}
                        pageSize={DEVICE_PAGE_SIZE}
                        totalRows={deviceTotal}
                        page={gatewayDevicesPagination.page}
                        onPageChange={(page: number) => {
                          gatewayDevicesPagination.onPageChange(page);
                          void loadDevicePage(selectedGateway, page, deviceFilter);
                        }}
                        previousAriaLabel={t("global.back")}
                        nextAriaLabel={t("global.next")}
                        emptyMessage={
                          deviceFilterActive ? (
                            <TableEmptyState
                              title={t("settingsPanel.gatewayPanel.noMatchingDevices")}
                              hint={t("settingsPanel.gatewayPanel.noMatchingDevicesHint")}
                              action={
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setDeviceFilter("");
                                    filterDevices("");
                                  }}
                                >
                                  {t("billingPanel.resetFilter")}
                                </Button>
                              }
                            />
                          ) : (
                            <TableEmptyState
                              title={t("settingsPanel.gatewayPanel.noDevices")}
                              hint={t("settingsPanel.gatewayPanel.noDevicesHint")}
                            />
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1 basis-[320px]">
                    {selectedDevices.length > 1 ? (
                      editDevice ? (
                        <DeviceProperties
                          hideIDField
                          configurationOnly={true}
                          device={editDevice}
                          applyConfiguration={updateConfigurations}
                        ></DeviceProperties>
                      ) : (
                        <p className="p-4 text-center text-sm text-muted-foreground">
                          {t("settingsPanel.gatewayPanel.heterogenousDeviceTypesSelected")}
                        </p>
                      )
                    ) : selectedDevices.length === 1 ? (
                      <>
                        {asset ? (
                          <div className="mb-6 min-w-0">
                            <h3 className="text-lg font-semibold">
                              {t("settingsPanel.gatewayPanel.deviceAsset")}
                            </h3>
                            <p className="mt-2 min-w-0 break-words text-sm">
                              {asset.street} {asset.streetNumber},{" "}
                              {asset.postalCode} {asset.city},{" "}
                              {asset.country}
                            </p>
                          </div>
                        ) : null}
                        <DeviceProperties
                          device={editDevice}
                          gatewayUrl={selectedGateway?.url}
                          assetKey={editDevice?._id ? String(editDevice._id) : undefined}
                          applyConfiguration={updateConfigurations}
                        ></DeviceProperties>
                      </>
                    ) : (
                      <p className="p-4 text-center text-sm text-muted-foreground">
                        {t("settingsPanel.gatewayPanel.noDeviceSelected")}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <>
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              <h2 className="min-w-0 flex-1 text-lg font-semibold">
                {t("settingsPanel.gatewayPanel.newGateway")}
              </h2>
              <div className="flex flex-wrap gap-2 max-sm:w-full">
                <Button
                  variant="outline"
                  className="max-sm:flex-1"
                  onClick={() => {
                    setSelectedGateway(null);
                  }}
                >
                  {t("settingsPanel.gatewayPanel.close")}
                </Button>
                <Button
                  className="max-sm:flex-1"
                  onClick={async () => {
                    await createGateway(selectedGateway);

                    setSelectedGateway(null);
                  }}
                >
                  {t("settingsPanel.gatewayPanel.create")}
                </Button>
              </div>
            </div>
            <div className="flex min-w-0 max-w-2xl flex-wrap items-end gap-4">
              <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
                <Label htmlFor={gatewayUrlId}>
                  {t("settingsPanel.gatewayPanel.awsUrl")}
                  <span aria-hidden className="ml-0.5 text-destructive">*</span>
                </Label>
                <Input
                  id={gatewayUrlId}
                  required
                  value={selectedGateway.url}
                  onChange={(event) => {
                    setSelectedGateway({ ...selectedGateway, url: event.target.value });
                  }}
                />
              </div>
              <div className="flex min-w-[200px] flex-col gap-1.5">
                <Label htmlFor={gatewayCustomerId}>{t("settingsPanel.gatewayPanel.customer")}</Label>
                <Select>
                  <SelectTrigger id={gatewayCustomerId} className="w-[300px] max-w-full">
                    <SelectValue placeholder={t("settingsPanel.gatewayPanel.selectOptions")} />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">
                    {(customers || []).map((customer: Customer) => (
                      <SelectItem key={customer._id} value={String(customer._id)} className="whitespace-normal">
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )
      ) : (
        <>
          <h2 className="text-lg font-semibold">
            {t("settingsPanel.gatewayPanel.gateways")}
          </h2>
          <div className="min-w-0 overflow-x-auto">
            <DataTable
              columns={columns}
              data={gateways}
              loading={gatewaysLoading}
              pageSize={20}
              page={gatewayListPagination.page}
              onPageChange={gatewayListPagination.onPageChange}
              onRowClick={(gateway) => selectGateway(gateway)}
              previousAriaLabel={t("global.back")}
              nextAriaLabel={t("global.next")}
              emptyMessage={
                <TableEmptyState
                  title={t("settingsPanel.gatewayPanel.noGateways")}
                  hint={t("settingsPanel.gatewayPanel.noGatewaysHint")}
                />
              }
            />
          </div>
        </>
      )}
      <Dialog
        open={showUploadRecordsDialog}
        onOpenChange={(open) => { if (!open) setShowUploadRecordsDialog(false); }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto sm:max-w-[1400px]">
          <DialogHeader>
            <DialogTitle>{t("settingsPanel.gatewayPanel.successfulUploadRecords")}</DialogTitle>
          </DialogHeader>
          <div className="min-w-0 overflow-x-auto">
            <DataTable
              columns={uploadRecordsColumns}
              data={gatewayStatus?.successfulUploadRecords || []}
              pageSize={10}
              page={uploadRecordsPagination.page}
              onPageChange={uploadRecordsPagination.onPageChange}
              previousAriaLabel={t("global.back")}
              nextAriaLabel={t("global.next")}
              emptyMessage={
                <TableEmptyState
                  title={t("settingsPanel.gatewayPanel.noUploadRecords")}
                  hint={t("settingsPanel.gatewayPanel.noUploadRecordsHint")}
                />
              }
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="max-sm:w-full"
              onClick={() => setShowUploadRecordsDialog(false)}
            >
              {t("settingsPanel.gatewayPanel.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
